/**
 * NewTicketPage — open a new support ticket.
 *
 * Route: `/support/new` (ProtectedRoute).
 *
 * Optionally reads `?relatedType=service_history&relatedId=<id>` from
 * the URL. ServiceHistoryPage's per-card "Contact support" link sets
 * those, so admins know which procedure the user is asking about.
 * (Transactions can use the same shape — `?relatedType=transaction` —
 * once the entry point is wired there.)
 *
 * On submit: useMutation → navigate to /support/:id (the new ticket's
 * detail page) so the user immediately sees what they posted.
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import Container from '@/components/PageSheet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SendIcon from '@mui/icons-material/Send';

import LanguageToggle from '@/components/LanguageToggle';
import { supportApi } from '@/services/api';
import { selectIsRtl } from '@/store/uiSlice';
import { APP_SUPPORT_CATEGORIES } from '@/utils/ticketCategories';

// App-support topics. These are platform-level: payments/transactions,
// problems with a clinic raised to the app team, the app itself, and the
// account. Service/medical concerns belong to the CLINIC stream instead
// (see NewClinicTicketPage). All values are valid backend TicketCategory codes.
// App-support topics (transactions / app / account / platform). Sourced from
// the shared taxonomy so the app↔clinic split stays in one place.
const CATEGORIES = APP_SUPPORT_CATEGORIES;

const SUBJECT_MAX = 120;
const MESSAGE_MAX = 2000;

export function NewTicketPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRtl = useSelector(selectIsRtl);
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const relatedType = searchParams.get('relatedType');
  const relatedId = searchParams.get('relatedId');

  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);

  const mutation = useMutation({
    mutationFn: () =>
      supportApi.createTicket({
        subject,
        category,
        message,
        relatedType: relatedType || null,
        relatedId: relatedId || null,
      }),
    onSuccess: ({ data }) => {
      // Invalidate the list so it refetches with the new ticket included.
      queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
      navigate(`/support/${data.ticket.id}`, { replace: true });
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'VALIDATION_ERROR') {
        setError(t('support.new.validation'));
      } else {
        setError(t('support.error.generic'));
      }
    },
  });

  const handleSubmit = () => {
    setError(null);
    if (!subject.trim()) {
      setError(t('support.new.subjectRequired'));
      return;
    }
    if (!message.trim()) {
      setError(t('support.new.messageRequired'));
      return;
    }
    mutation.mutate();
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/support');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Stack spacing={3}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              size="small"
              onClick={handleBack}
              aria-label={t('common.back')}
              disabled={mutation.isPending}
            >
              {isRtl ? <ArrowForwardIcon /> : <ArrowBackIcon />}
            </IconButton>
            <Typography variant="h6" component="h1" fontWeight={700}>
              {t('support.new.title')}
            </Typography>
          </Stack>
          <LanguageToggle />
        </Stack>

        {/* Context banner — shows when arriving from service-history (or
            a future transaction entry point) so the user knows the
            ticket is linked to that record. */}
        {relatedType && relatedId && (
          <Card variant="outlined" sx={{ bgcolor: 'primary.50' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Chip
                  size="small"
                  label={t(`support.new.related.${relatedType}`, {
                    defaultValue: relatedType,
                  })}
                  variant="outlined"
                />
                <Typography variant="caption" color="text.secondary">
                  {t('support.new.relatedHint')}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Form */}
        <Stack spacing={2}>
          <TextField
            label={t('support.new.subject')}
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              if (error) setError(null);
            }}
            disabled={mutation.isPending}
            fullWidth
            inputProps={{ maxLength: SUBJECT_MAX, 'aria-label': t('support.new.subject') }}
            helperText={`${subject.length}/${SUBJECT_MAX}`}
          />

          <TextField
            select
            label={t('support.new.category')}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            disabled={mutation.isPending}
            fullWidth
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {t(`support.category.${c}`)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label={t('support.new.message')}
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              if (error) setError(null);
            }}
            disabled={mutation.isPending}
            fullWidth
            multiline
            rows={6}
            inputProps={{ maxLength: MESSAGE_MAX, 'aria-label': t('support.new.message') }}
            helperText={`${message.length}/${MESSAGE_MAX}`}
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={mutation.isPending}
              sx={{ flex: 1 }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={mutation.isPending}
              startIcon={
                mutation.isPending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
              sx={{ flex: 2 }}
            >
              {mutation.isPending
                ? t('support.new.submitting')
                : t('support.new.submit')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
}

export default NewTicketPage;
